import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Briefcase,
  ArrowRight,
  Home,
  TrendingUp,
  Heart,
  Laptop,
  Users,
  CheckCircle2,
  Sparkles,
  Brain,
  Code2,
  Rocket,
  Coffee,
  Globe,
  GraduationCap,
  Award,
  Clock,
  Send,
  Upload,
  Mail,
  User,
  FileText,
  Zap,
  Target,
  Lightbulb,
  Shield,
  Star,
  MapPin,
  Building2,
  Trophy,
} from 'lucide-react';

// Animation component props type
interface AnimationProps {
  t: (key: string, options?: Record<string, unknown>) => string;
}

// Innovation Animation Component - Creative Lab Visualization
function InnovationAnimation({ t }: AnimationProps) {
  const [activeNode, setActiveNode] = useState(0);
  const [pulseRings, setPulseRings] = useState<number[]>([]);
  const [codeLines, setCodeLines] = useState([0, 1]);
  const [ideaFlow, setIdeaFlow] = useState(0);

  useEffect(() => {
    const nodeInterval = setInterval(() => {
      setActiveNode(prev => (prev + 1) % 6);
    }, 1200);

    const pulseInterval = setInterval(() => {
      setPulseRings(prev => [...prev.slice(-2), Date.now()]);
    }, 2000);

    const codeInterval = setInterval(() => {
      setCodeLines(prev => prev.map(l => (l + 1) % 5));
    }, 600);

    const flowInterval = setInterval(() => {
      setIdeaFlow(prev => (prev + 1) % 100);
    }, 50);

    return () => {
      clearInterval(nodeInterval);
      clearInterval(pulseInterval);
      clearInterval(codeInterval);
      clearInterval(flowInterval);
    };
  }, []);

  return (
    <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a] rounded-xl p-3 aspect-square relative overflow-hidden">
      {/* Neural Network Background */}
      <svg className="absolute inset-0 w-full h-full opacity-20">
        <defs>
          <linearGradient id="innovGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        {/* Connection lines */}
        {[...Array(8)].map((_, i) => (
          <line
            key={i}
            x1={`${20 + (i % 3) * 30}%`}
            y1={`${20 + Math.floor(i / 3) * 25}%`}
            x2={`${35 + ((i + 1) % 3) * 25}%`}
            y2={`${30 + Math.floor((i + 1) / 3) * 20}%`}
            stroke="url(#innovGrad)"
            strokeWidth="1"
            className={activeNode === i % 6 ? 'opacity-80' : 'opacity-20'}
          />
        ))}
      </svg>

      {/* Pulse Rings */}
      {pulseRings.map((id) => (
        <div
          key={id}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full border-2 border-yellow-500/30 animate-ping"
          style={{ animationDuration: '2s' }}
        />
      ))}

      {/* Central Innovation Hub */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center">
        {/* Rotating Outer Ring */}
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-2 border-dashed border-orange-500/30 animate-spin" style={{ animationDuration: '8s' }} />
          <div className="absolute inset-1.5 w-17 h-17 rounded-full bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <div className="text-white text-center">
              <div className="text-xl font-bold">{Math.floor(ideaFlow / 10)}</div>
              <div className="text-[7px] opacity-80">{t('careers.animations.ideasPerSec')}</div>
            </div>
          </div>
        </div>

        {/* Floating Tech Nodes */}
        <div className="absolute inset-0">
          {['AI', 'ML', 'IoT', 'AR', 'VR', 'Web3'].map((tech, i) => {
            const angle = (i * 60 - 90) * (Math.PI / 180);
            const radius = 40;
            const x = 50 + radius * Math.cos(angle);
            const y = 50 + radius * Math.sin(angle);
            return (
              <div
                key={tech}
                className={`absolute w-7 h-7 rounded-lg flex items-center justify-center text-[8px] font-bold transition-all duration-500 ${
                  activeNode === i
                    ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white scale-125 shadow-lg shadow-orange-500/50'
                    : 'bg-white/10 text-white/60'
                }`}
                style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
              >
                {tech}
              </div>
            );
          })}
        </div>

        {/* Bottom Stats */}
        <div className="absolute bottom-1.5 left-1.5 right-1.5 flex justify-between">
          <div className="bg-white/5 rounded px-1.5 py-0.5">
            <div className="text-[6px] text-white/40">{t('careers.animations.rnd')}</div>
            <div className="text-[9px] text-orange-400 font-bold">+45%</div>
          </div>
          <div className="bg-white/5 rounded px-1.5 py-0.5">
            <div className="text-[6px] text-white/40">{t('careers.animations.patent')}</div>
            <div className="text-[9px] text-yellow-400 font-bold">12+</div>
          </div>
          <div className="bg-white/5 rounded px-1.5 py-0.5">
            <div className="text-[6px] text-white/40">{t('careers.animations.technology')}</div>
            <div className="text-[9px] text-red-400 font-bold">50+</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reliability Animation Component - Trust Building Dashboard
function ReliabilityAnimation({ t }: AnimationProps) {
  const [uptimeProgress, setUptimeProgress] = useState(0);
  const [satisfactionScore, setSatisfactionScore] = useState(0);
  const [activeProject, setActiveProject] = useState(0);
  const [serverStatus, setServerStatus] = useState([true, true, true, true]);

  useEffect(() => {
    const uptimeInterval = setInterval(() => {
      setUptimeProgress(prev => prev < 99.9 ? prev + 0.1 : 99.9);
    }, 30);

    const satisfactionInterval = setInterval(() => {
      setSatisfactionScore(prev => prev < 100 ? prev + 1 : 100);
    }, 40);

    const projectInterval = setInterval(() => {
      setActiveProject(prev => (prev + 1) % 4);
    }, 2000);

    const serverInterval = setInterval(() => {
      setServerStatus(prev => {
        const next = [...prev];
        const idx = Math.floor(Math.random() * 4);
        next[idx] = Math.random() > 0.1;
        return next;
      });
    }, 1500);

    return () => {
      clearInterval(uptimeInterval);
      clearInterval(satisfactionInterval);
      clearInterval(projectInterval);
      clearInterval(serverInterval);
    };
  }, []);

  const completedStatus = t('careers.animations.completed');
  const inProgressStatus = t('careers.animations.inProgress');
  const projects = [
    { name: 'Fintech App', client: 'ABC Bank', status: completedStatus, isCompleted: true },
    { name: 'E-commerce', client: 'XYZ Ltd.', status: completedStatus, isCompleted: true },
    { name: 'CRM System', client: 'Delta Corp.', status: completedStatus, isCompleted: true },
    { name: 'AI Platform', client: 'Tech Inc.', status: inProgressStatus, isCompleted: false },
  ];

  return (
    <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a1a0a] rounded-xl p-3 aspect-square relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="absolute w-full h-px bg-green-500" style={{ top: `${i * 20}%` }} />
        ))}
        {[...Array(6)].map((_, i) => (
          <div key={i} className="absolute h-full w-px bg-green-500" style={{ left: `${i * 20}%` }} />
        ))}
      </div>

      <div className="relative z-10 h-full flex flex-col">
        {/* Header Stats */}
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="text-[9px] text-white/40 uppercase tracking-wider">{t('careers.animations.uptime')}</div>
            <div className="text-lg font-bold text-green-400">{uptimeProgress.toFixed(1)}%</div>
          </div>
          <div className="flex gap-0.5">
            {serverStatus.map((status, i) => (
              <div key={i} className={`w-1.5 h-5 rounded-sm ${status ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
            ))}
          </div>
        </div>

        {/* Satisfaction Meter */}
        <div className="bg-white/5 rounded-lg p-2 mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-[9px] text-white/60">{t('careers.animations.customerSatisfaction')}</span>
            <span className="text-xs font-bold text-emerald-400">{satisfactionScore}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-100"
              style={{ width: `${satisfactionScore}%` }}
            />
          </div>
        </div>

        {/* Project Timeline */}
        <div className="flex-1 space-y-1 min-h-0 overflow-hidden">
          <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1">{t('careers.animations.recentProjects')}</div>
          {projects.map((project, i) => (
            <div
              key={i}
              className={`flex items-center justify-between p-1.5 rounded-lg transition-all duration-300 ${
                activeProject === i ? 'bg-green-500/20 border border-green-500/30' : 'bg-white/5'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${project.isCompleted ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
                <div>
                  <div className="text-[9px] text-white font-medium">{project.name}</div>
                  <div className="text-[7px] text-white/40">{project.client}</div>
                </div>
              </div>
              <div className={`text-[7px] px-1.5 py-0.5 rounded ${project.isCompleted ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {project.status}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Trust Indicators */}
        <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
          <div className="text-center">
            <div className="text-sm font-bold text-white">50+</div>
            <div className="text-[7px] text-white/40">{t('careers.animations.project')}</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold text-white">8+</div>
            <div className="text-[7px] text-white/40">{t('careers.animations.years')}</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold text-white">100%</div>
            <div className="text-[7px] text-white/40">{t('careers.animations.delivery')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Excellence Animation Component - Terminal Style Quality Check
function ExcellenceAnimation({ t }: AnimationProps) {
  const [activeCheck, setActiveCheck] = useState(-1);
  const [allComplete, setAllComplete] = useState(false);
  const [cursorBlink, setCursorBlink] = useState(true);

  useEffect(() => {
    // Animate through checks
    const checkInterval = setInterval(() => {
      setActiveCheck(prev => {
        if (prev >= 5) {
          setAllComplete(true);
          return -1;
        }
        setAllComplete(false);
        return prev + 1;
      });
    }, 800);

    // Cursor blink
    const cursorInterval = setInterval(() => {
      setCursorBlink(prev => !prev);
    }, 500);

    return () => {
      clearInterval(checkInterval);
      clearInterval(cursorInterval);
    };
  }, []);

  const checks = [
    { label: t('careers.animations.codeAnalysis'), score: '98%', icon: '◆' },
    { label: t('careers.animations.testCoverage'), score: '95%', icon: '◆' },
    { label: t('careers.animations.performance'), score: '99%', icon: '◆' },
    { label: t('careers.animations.security'), score: '97%', icon: '◆' },
    { label: t('careers.animations.bestPractices'), score: 'OK', icon: '◆' },
    { label: t('careers.animations.build'), score: 'OK', icon: '◆' },
  ];

  return (
    <div className="bg-gradient-to-br from-[#0d1117] to-[#161b22] rounded-xl p-2 aspect-square relative overflow-hidden font-mono">
      {/* Terminal Header */}
      <div className="flex items-center gap-1.5 mb-2 shrink-0">
        <div className="w-2 h-2 rounded-full bg-red-500" />
        <div className="w-2 h-2 rounded-full bg-yellow-500" />
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-[7px] text-white/40 ml-2">quality-check.sh</span>
      </div>

      {/* Terminal Content */}
      <div className="h-[calc(100%-24px)] flex flex-col">
        {/* Command Line */}
        <div className="text-[8px] text-green-400 mb-1.5 shrink-0">
          <span className="text-purple-400">$</span> npm run quality-check
        </div>

        {/* Check Items */}
        <div className="flex-1 space-y-1 min-h-0">
          {checks.map((check, i) => {
            const isComplete = i < activeCheck || allComplete;
            const isActive = i === activeCheck && !allComplete;

            return (
              <div
                key={i}
                className={`flex items-center justify-between text-[8px] transition-all duration-300 ${
                  isComplete ? 'opacity-100' : isActive ? 'opacity-100' : 'opacity-30'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  {isComplete ? (
                    <span className="text-green-400">✓</span>
                  ) : isActive ? (
                    <span className="text-yellow-400 animate-pulse">●</span>
                  ) : (
                    <span className="text-white/20">○</span>
                  )}
                  <span className={isComplete ? 'text-white/80' : isActive ? 'text-white' : 'text-white/40'}>
                    {check.label}
                  </span>
                </div>
                {isComplete && (
                  <span className="text-green-400 font-bold">{check.score}</span>
                )}
                {isActive && (
                  <span className="text-yellow-400">{t('careers.animations.checking')}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Result Footer */}
        <div className="mt-1.5 pt-1.5 border-t border-white/10 shrink-0">
          {allComplete ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-green-400 text-sm">✓</span>
                <span className="text-[9px] text-green-400 font-bold">{t('careers.animations.allChecksPassed')}</span>
              </div>
              <div className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-[8px] font-bold">
                A+
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-[8px] text-white/40">
              <span className="text-purple-400">$</span>
              <span>{cursorBlink ? '▊' : ''}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Global Vision Animation Component - Orbital Countries Visualization
function GlobalVisionAnimation({ t }: AnimationProps) {
  const [activeCountry, setActiveCountry] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [projectCount, setProjectCount] = useState(0);

  useEffect(() => {
    const countryInterval = setInterval(() => {
      setActiveCountry(prev => (prev + 1) % 9);
    }, 2000);

    const rotateInterval = setInterval(() => {
      setRotation(prev => (prev + 1) % 360);
    }, 50);

    const countInterval = setInterval(() => {
      setProjectCount(prev => prev < 50 ? prev + 1 : 50);
    }, 50);

    return () => {
      clearInterval(countryInterval);
      clearInterval(rotateInterval);
      clearInterval(countInterval);
    };
  }, []);

  const countryNames = t('careers.animations.countries', { returnObjects: true }) as unknown as Record<string, string>;
  const countries = [
    { name: countryNames.turkey || 'Turkey', flag: '🇹🇷', projects: 15 },
    { name: countryNames.usa || 'USA', flag: '🇺🇸', projects: 8 },
    { name: countryNames.germany || 'Germany', flag: '🇩🇪', projects: 6 },
    { name: countryNames.uk || 'UK', flag: '🇬🇧', projects: 5 },
    { name: countryNames.france || 'France', flag: '🇫🇷', projects: 4 },
    { name: countryNames.netherlands || 'Netherlands', flag: '🇳🇱', projects: 3 },
    { name: countryNames.belgium || 'Belgium', flag: '🇧🇪', projects: 3 },
    { name: countryNames.switzerland || 'Switzerland', flag: '🇨🇭', projects: 3 },
    { name: countryNames.uae || 'UAE', flag: '🇦🇪', projects: 3 },
  ];

  return (
    <div className="bg-gradient-to-br from-[#0a1a2e] to-[#1a1a3e] rounded-xl p-2 aspect-square relative overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(5)].map((_, i) => (
          <div key={`h-${i}`} className="absolute w-full h-px bg-cyan-500" style={{ top: `${(i + 1) * 20}%` }} />
        ))}
        {[...Array(5)].map((_, i) => (
          <div key={`v-${i}`} className="absolute h-full w-px bg-cyan-500" style={{ left: `${(i + 1) * 20}%` }} />
        ))}
      </div>

      <div className="relative z-10 h-full flex flex-col">
        {/* Header with Active Country */}
        <div className="flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1">
            <span className="text-sm">{countries[activeCountry].flag}</span>
            <div>
              <span className="text-[8px] text-white font-medium">{countries[activeCountry].name}</span>
              <span className="text-[7px] text-cyan-400 ml-1">({countries[activeCountry].projects} {t('careers.animations.projects')})</span>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-[7px] text-cyan-400">{t('careers.animations.live')}</span>
          </div>
        </div>

        {/* Orbital Visualization */}
        <div className="flex-1 relative flex items-center justify-center min-h-0 my-1">
          {/* Orbit Rings */}
          <svg className="absolute w-full h-full" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(34, 211, 238, 0.1)" strokeWidth="0.5" />
            <circle cx="50" cy="50" r="28" fill="none" stroke="rgba(34, 211, 238, 0.15)" strokeWidth="0.5" strokeDasharray="3 3" />
            {/* Connection lines to active country */}
            {countries.map((_, i) => {
              const angle = (i * 40 + rotation * 0.2 - 90) * (Math.PI / 180);
              const x = 50 + 38 * Math.cos(angle);
              const y = 50 + 38 * Math.sin(angle);
              return (
                <line
                  key={i}
                  x1="50"
                  y1="50"
                  x2={x}
                  y2={y}
                  stroke={activeCountry === i ? 'rgba(34, 211, 238, 0.4)' : 'rgba(34, 211, 238, 0.1)'}
                  strokeWidth="0.5"
                />
              );
            })}
          </svg>

          {/* Center Hub */}
          <div className="relative z-10 w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex flex-col items-center justify-center shadow-lg shadow-cyan-500/30">
            <div className="text-lg font-bold text-white">{projectCount}+</div>
            <div className="text-[6px] text-white/80">{t('careers.animations.project').toUpperCase()}</div>
          </div>

          {/* Orbiting Countries */}
          {countries.map((country, i) => {
            const angle = (i * 40 + rotation * 0.2 - 90) * (Math.PI / 180);
            const radius = 38;
            const x = 50 + radius * Math.cos(angle);
            const y = 50 + radius * Math.sin(angle);
            return (
              <div
                key={i}
                className="absolute transition-all duration-300"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {activeCountry === i && (
                  <div className="absolute inset-0 w-6 h-6 -m-1 rounded-full bg-cyan-500/30 animate-ping" />
                )}
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                    activeCountry === i
                      ? 'bg-cyan-500 scale-125 shadow-lg shadow-cyan-500/50'
                      : 'bg-white/10'
                  }`}
                >
                  <span className="text-[10px]">{country.flag}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Stats */}
        <div className="flex justify-between gap-1 shrink-0">
          <div className="flex-1 bg-white/5 rounded px-1.5 py-1 text-center">
            <div className="text-xs font-bold text-cyan-400">9</div>
            <div className="text-[6px] text-white/40">{t('careers.animations.country')}</div>
          </div>
          <div className="flex-1 bg-white/5 rounded px-1.5 py-1 text-center">
            <div className="text-xs font-bold text-blue-400">50+</div>
            <div className="text-[6px] text-white/40">{t('careers.animations.project')}</div>
          </div>
          <div className="flex-1 bg-white/5 rounded px-1.5 py-1 text-center">
            <div className="text-xs font-bold text-teal-400">8+</div>
            <div className="text-[6px] text-white/40">{t('careers.animations.years')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Team Spirit Animation Component - Collaborative Culture
function TeamSpiritAnimation({ t }: AnimationProps) {
  const [activeTeamMember, setActiveTeamMember] = useState(0);
  const [collaborationLines, setCollaborationLines] = useState<number[]>([]);
  const [messageCount, setMessageCount] = useState(0);

  useEffect(() => {
    const memberInterval = setInterval(() => {
      setActiveTeamMember(prev => (prev + 1) % 6);
    }, 1500);

    const lineInterval = setInterval(() => {
      setCollaborationLines(prev => [...prev.slice(-3), Date.now()]);
    }, 2000);

    const msgInterval = setInterval(() => {
      setMessageCount(prev => prev < 247 ? prev + 3 : 247);
    }, 30);

    return () => {
      clearInterval(memberInterval);
      clearInterval(lineInterval);
      clearInterval(msgInterval);
    };
  }, []);

  const teamMembers = [
    { role: 'Lead', color: 'from-purple-500 to-violet-500', pos: { x: 50, y: 20 } },
    { role: 'Dev', color: 'from-blue-500 to-cyan-500', pos: { x: 25, y: 45 } },
    { role: 'Dev', color: 'from-green-500 to-emerald-500', pos: { x: 75, y: 45 } },
    { role: 'Design', color: 'from-pink-500 to-rose-500', pos: { x: 15, y: 75 } },
    { role: 'QA', color: 'from-orange-500 to-amber-500', pos: { x: 50, y: 80 } },
    { role: 'DevOps', color: 'from-red-500 to-orange-500', pos: { x: 85, y: 75 } },
  ];

  return (
    <div className="bg-gradient-to-br from-[#1a1a2e] to-[#2a1a3e] rounded-xl p-3 aspect-square relative overflow-hidden">
      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="teamGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#EC4899" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {teamMembers.map((member, i) =>
          teamMembers.slice(i + 1).map((target, j) => (
            <line
              key={`${i}-${j}`}
              x1={`${member.pos.x}%`}
              y1={`${member.pos.y}%`}
              x2={`${target.pos.x}%`}
              y2={`${target.pos.y}%`}
              stroke="url(#teamGrad)"
              strokeWidth="1"
              className={collaborationLines.length > j % 4 ? 'opacity-60' : 'opacity-20'}
            />
          ))
        )}
      </svg>

      <div className="relative z-10 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="text-[8px] text-white/40 uppercase tracking-wider">{t('careers.animations.teamCollaboration')}</div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
            <span className="text-[7px] text-purple-400">{messageCount} {t('careers.animations.messages')}</span>
          </div>
        </div>

        {/* Team Network */}
        <div className="flex-1 relative min-h-0 my-1">
          {teamMembers.map((member, i) => (
            <div
              key={i}
              className="absolute transition-all duration-500"
              style={{ left: `${member.pos.x}%`, top: `${member.pos.y}%`, transform: 'translate(-50%, -50%)' }}
            >
              {activeTeamMember === i && (
                <div className="absolute inset-0 w-8 h-8 -m-2 rounded-full bg-purple-500/20 animate-ping" />
              )}
              <div
                className={`w-7 h-7 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center transition-all duration-300 ${
                  activeTeamMember === i ? 'scale-125 shadow-lg' : ''
                }`}
              >
                <span className="text-[7px] text-white font-bold">{member.role}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-1">
          <div className="bg-white/5 rounded p-1 text-center">
            <div className="text-xs font-bold text-purple-400">15+</div>
            <div className="text-[6px] text-white/40">{t('careers.animations.experts')}</div>
          </div>
          <div className="bg-white/5 rounded p-1 text-center">
            <div className="text-xs font-bold text-pink-400">6</div>
            <div className="text-[6px] text-white/40">{t('careers.animations.departments')}</div>
          </div>
          <div className="bg-white/5 rounded p-1 text-center">
            <div className="text-xs font-bold text-violet-400">24/7</div>
            <div className="text-[6px] text-white/40">{t('careers.animations.communication')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon mappings for values
const valueIcons: Record<string, typeof Lightbulb> = {
  innovation: Lightbulb,
  reliability: Shield,
  excellence: Target,
  globalVision: Globe,
  teamSpirit: Users,
};

// Color mappings for values
const valueColors: Record<string, string> = {
  innovation: 'from-yellow-500 to-orange-500',
  reliability: 'from-green-500 to-emerald-500',
  excellence: 'from-purple-500 to-fuchsia-500',
  globalVision: 'from-cyan-500 to-blue-500',
  teamSpirit: 'from-violet-500 to-purple-500',
};

// Animation mappings for values
const valueAnimations: Record<string, React.ComponentType<AnimationProps>> = {
  innovation: InnovationAnimation,
  reliability: ReliabilityAnimation,
  excellence: ExcellenceAnimation,
  globalVision: GlobalVisionAnimation,
  teamSpirit: TeamSpiritAnimation,
};

// Icon mappings for benefits
const benefitIcons: Record<string, typeof Home> = {
  flexible: Home,
  growth: TrendingUp,
  innovation: Brain,
  health: Heart,
  team: Users,
  tech: Laptop,
};

// Color mappings for benefits
const benefitColors: Record<string, string> = {
  flexible: 'from-blue-500 to-cyan-500',
  growth: 'from-green-500 to-emerald-500',
  innovation: 'from-purple-500 to-violet-500',
  health: 'from-red-500 to-rose-500',
  team: 'from-orange-500 to-amber-500',
  tech: 'from-indigo-500 to-blue-500',
};

// Icon mappings for expertise areas
const expertiseIcons: Record<string, typeof Brain> = {
  ai: Brain,
  software: Code2,
  security: Shield,
  devops: Rocket,
  design: Zap,
  research: GraduationCap,
};

// Color mappings for expertise areas
const expertiseColors: Record<string, string> = {
  ai: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  software: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  security: 'bg-red-500/10 text-red-400 border-red-500/20',
  devops: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  design: 'bg-green-500/10 text-green-400 border-green-500/20',
  research: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
};

export function CareersPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    expertise: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeValue, setActiveValue] = useState(0);

  // Translated values with animations
  const valueKeys = ['innovation', 'reliability', 'excellence', 'globalVision', 'teamSpirit'];
  const valuesWithAnimations = valueKeys.map((key) => {
    const valueData = t(`careers.values.items.${key}`, { returnObjects: true }) as {
      title: string;
      shortDesc: string;
      description: string;
      details: string[];
    };
    return {
      id: key,
      icon: valueIcons[key],
      title: valueData.title,
      shortDesc: valueData.shortDesc,
      description: valueData.description,
      color: valueColors[key],
      animation: valueAnimations[key],
      details: valueData.details,
    };
  });

  // Translated benefits
  const benefitKeys = ['flexible', 'growth', 'innovation', 'health', 'team', 'tech'];
  const benefits = benefitKeys.map((key) => {
    const benefitData = t(`careers.benefits.items.${key}`, { returnObjects: true }) as {
      title: string;
      description: string;
    };
    return {
      icon: benefitIcons[key],
      title: benefitData.title,
      description: benefitData.description,
      color: benefitColors[key],
    };
  });

  // Translated expertise areas
  const expertiseKeys = ['ai', 'software', 'security', 'devops', 'design', 'research'];
  const expertiseLabels = t('careers.expertise.areas', { returnObjects: true }) as Record<string, string>;
  const expertiseAreas = expertiseKeys.map((key) => ({
    icon: expertiseIcons[key],
    label: expertiseLabels[key] || key,
    color: expertiseColors[key],
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ name: '', email: '', expertise: '', message: '' });
  };

  return (
    <>
      <SEO {...SEO_CONFIG.careers} />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-lg relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />

          {/* Floating Icons */}
          <div className="absolute top-1/4 left-1/4 text-accent/20 animate-bounce" style={{ animationDuration: '3s' }}>
            <Code2 size={40} />
          </div>
          <div className="absolute top-1/3 right-1/4 text-purple-500/20 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
            <Brain size={50} />
          </div>
          <div className="absolute bottom-1/3 left-1/3 text-cyan-500/20 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>
            <Rocket size={35} />
          </div>
        </div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Sparkles size={16} />
              <span>{t('careers.hero.badge')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {t('careers.hero.title')}{' '}
              <span className="bg-gradient-to-r from-accent via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                {t('careers.hero.titleHighlight')}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-text-secondary max-w-2xl mx-auto mb-8">
              {t('careers.hero.description')}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="#basvuru" className="btn btn-primary">
                {t('careers.buttons.apply')}
                <ArrowRight size={18} />
              </a>
              <a href="#neden-biz" className="btn btn-secondary">
                {t('careers.buttons.whyUs')}
              </a>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-12 pt-8 border-t border-border-default/50">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">8+</div>
                <div className="text-sm text-text-secondary">{t('careers.stats.yearsExperience')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400">9</div>
                <div className="text-sm text-text-secondary">{t('careers.stats.countriesProjects')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400">50+</div>
                <div className="text-sm text-text-secondary">{t('careers.stats.completedProjects')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">100%</div>
                <div className="text-sm text-text-secondary">{t('careers.stats.clientSatisfaction')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section id="neden-biz" className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm mb-4">
              <Heart size={14} />
              <span>{t('careers.benefits.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('careers.benefits.title')}</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('careers.benefits.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group card p-6 hover:border-accent/30 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <benefit.icon className="text-white" size={28} />
                </div>
                <h3 className="font-semibold text-lg mb-2 group-hover:text-accent transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values - Interactive Section */}
      <section className="section">
        <div className="container">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-sm mb-4">
              <Award size={14} />
              <span>{t('careers.values.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('careers.values.title')}{' '}
              <span className="bg-gradient-to-r from-accent to-purple-500 bg-clip-text text-transparent">{t('careers.values.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('careers.values.description')}
            </p>
          </div>

          {/* Interactive Values Grid */}
          <div className="grid lg:grid-cols-[280px_1fr] gap-8">
            {/* Side Menu */}
            <div className="space-y-3">
              {valuesWithAnimations.map((value, index) => (
                <button
                  key={value.id}
                  onClick={() => setActiveValue(index)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-300 ${
                    activeValue === index
                      ? 'bg-gradient-to-r from-accent/20 to-purple-500/10 border border-accent/30 shadow-lg shadow-accent/10'
                      : 'bg-bg-surface/50 border border-border-default/50 hover:border-accent/20 hover:bg-bg-surface'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${activeValue === index ? 'scale-110' : ''}`}>
                    <value.icon className="text-white" size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold transition-colors ${activeValue === index ? 'text-accent' : 'text-text-primary'}`}>
                      {value.title}
                    </h4>
                    <p className="text-text-tertiary text-sm truncate">{value.shortDesc}</p>
                  </div>
                  {activeValue === index && (
                    <div className="w-1.5 h-8 rounded-full bg-gradient-to-b from-accent to-purple-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className="card border-accent/20 overflow-hidden">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Animation */}
                <div className="order-2 md:order-1">
                  {(() => {
                    const AnimationComponent = valuesWithAnimations[activeValue].animation;
                    return <AnimationComponent t={t} />;
                  })()}
                </div>

                {/* Details */}
                <div className="order-1 md:order-2 flex flex-col justify-center">
                  <div className="mb-2">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${valuesWithAnimations[activeValue].color} text-white`}>
                      {valuesWithAnimations[activeValue].shortDesc}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{valuesWithAnimations[activeValue].title}</h3>
                  <p className="text-text-secondary mb-6 text-sm leading-relaxed">
                    {valuesWithAnimations[activeValue].description}
                  </p>

                  {/* Feature List */}
                  <div className="space-y-2">
                    {valuesWithAnimations[activeValue].details.map((detail, i) => (
                      <div key={i} className="flex items-start gap-3 text-text-secondary group">
                        <div className={`w-5 h-5 rounded-md bg-gradient-to-br ${valuesWithAnimations[activeValue].color} flex items-center justify-center flex-shrink-0 mt-0.5 opacity-80 group-hover:opacity-100 transition-opacity`}>
                          <CheckCircle2 size={12} className="text-white" />
                        </div>
                        <span className="text-sm">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Areas We're Looking For */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-sm mb-4">
              <Target size={14} />
              <span>{t('careers.expertise.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('careers.expertise.title')}</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('careers.expertise.description')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {expertiseAreas.map((area, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 px-5 py-3 rounded-xl border ${area.color} hover:scale-105 transition-transform cursor-default`}
              >
                <area.icon size={20} />
                <span className="font-medium">{area.label}</span>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center p-6 rounded-2xl bg-bg-primary border border-border-default">
              <GraduationCap className="mx-auto mb-3 text-accent" size={32} />
              <h4 className="font-semibold mb-2">{t('careers.expertise.profiles.graduates.title')}</h4>
              <p className="text-text-secondary text-sm">
                {t('careers.expertise.profiles.graduates.description')}
              </p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-bg-primary border border-border-default">
              <Coffee className="mx-auto mb-3 text-purple-400" size={32} />
              <h4 className="font-semibold mb-2">{t('careers.expertise.profiles.experienced.title')}</h4>
              <p className="text-text-secondary text-sm">
                {t('careers.expertise.profiles.experienced.description')}
              </p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-bg-primary border border-border-default">
              <Clock className="mx-auto mb-3 text-cyan-400" size={32} />
              <h4 className="font-semibold mb-2">{t('careers.expertise.profiles.parttime.title')}</h4>
              <p className="text-text-secondary text-sm">
                {t('careers.expertise.profiles.parttime.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="basvuru" className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-8">
              {/* Form Info */}
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-sm mb-4">
                  <Send size={14} />
                  <span>{t('careers.form.badge')}</span>
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  {t('careers.form.title')}{' '}
                  <span className="text-accent">{t('careers.form.titleHighlight')}</span>
                </h2>
                <p className="text-text-secondary mb-6">
                  {t('careers.form.description')}
                </p>

                <div className="space-y-4">
                  {(t('careers.form.promises', { returnObjects: true }) as string[]).map((promise, index) => (
                    <div key={index} className="flex items-center gap-3 text-text-secondary">
                      <CheckCircle2 className="text-green-400" size={20} />
                      <span>{promise}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-3">
                <div className="card p-6 md:p-8">
                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="text-green-400" size={32} />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{t('careers.form.success.title')}</h3>
                      <p className="text-text-secondary mb-6">
                        {t('careers.form.success.description')}
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="btn btn-secondary"
                      >
                        {t('careers.buttons.newApplication')}
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <User size={14} className="inline mr-2" />
                          {t('careers.form.fields.name.label')}
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors"
                          placeholder={t('careers.form.fields.name.placeholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <Mail size={14} className="inline mr-2" />
                          {t('careers.form.fields.email.label')}
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors"
                          placeholder={t('careers.form.fields.email.placeholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <Briefcase size={14} className="inline mr-2" />
                          {t('careers.form.fields.expertise.label')}
                        </label>
                        <div className="relative">
                          <select
                            required
                            value={formData.expertise}
                            onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors appearance-none cursor-pointer pr-10"
                          >
                            <option value="">{t('careers.form.fields.expertise.placeholder')}</option>
                            <option value="ai">{t('careers.form.fields.expertise.options.ai')}</option>
                            <option value="frontend">{t('careers.form.fields.expertise.options.frontend')}</option>
                            <option value="backend">{t('careers.form.fields.expertise.options.backend')}</option>
                            <option value="fullstack">{t('careers.form.fields.expertise.options.fullstack')}</option>
                            <option value="mobile">{t('careers.form.fields.expertise.options.mobile')}</option>
                            <option value="devops">{t('careers.form.fields.expertise.options.devops')}</option>
                            <option value="security">{t('careers.form.fields.expertise.options.security')}</option>
                            <option value="design">{t('careers.form.fields.expertise.options.design')}</option>
                            <option value="pm">{t('careers.form.fields.expertise.options.pm')}</option>
                            <option value="other">{t('careers.form.fields.expertise.options.other')}</option>
                          </select>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <FileText size={14} className="inline mr-2" />
                          {t('careers.form.fields.message.label')}
                        </label>
                        <textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-3 rounded-xl bg-bg-surface border border-border-default focus:border-accent focus:outline-none transition-colors resize-none"
                          placeholder={t('careers.form.fields.message.placeholder')}
                        />
                      </div>

                      <div className="p-4 rounded-xl bg-bg-surface/50 border border-dashed border-border-default text-center">
                        <Upload className="mx-auto mb-2 text-text-tertiary" size={24} />
                        <p className="text-sm text-text-secondary">
                          {t('careers.form.cvNote')}{' '}
                          <a href="mailto:careers@bosphoruslabs.com" className="text-accent hover:underline">
                            careers@bosphoruslabs.com
                          </a>
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full btn btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {t('careers.buttons.submitting')}
                          </>
                        ) : (
                          <>
                            {t('careers.buttons.submit')}
                            <Send size={18} />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-accent/10 via-purple-500/5 to-cyan-500/10 border border-accent/20 p-8 md:p-12">
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl" />

              <div className="relative text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="text-white" size={32} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  {t('careers.cta.title')}
                </h2>
                <p className="text-text-secondary mb-8 max-w-xl mx-auto">
                  {t('careers.cta.description')}
                </p>
                <Link to="/contact" className="btn btn-primary">
                  {t('careers.buttons.contact')}
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
